/**
 * Course-level rules shared by the API and both sides of the web app (training redesign).
 */

import type { TrainingContentKind } from '../enums.js';

export interface TrainingStrictFlags {
  readonly requiresWebcam: boolean;
  readonly blocksClockOut: boolean;
  readonly strictPlayback: boolean;
}

/**
 * "Strict mode" is any of the three switches under a course's Advanced settings. A strict course
 * needs uploaded videos (no YouTube/Vimeo — decision 8.3), shows the webcam card when it requires a
 * camera, and is what makes the Today monitor and Overruns reports appear.
 */
export function isStrictTrainingCourse(flags: TrainingStrictFlags): boolean {
  return flags.requiresWebcam || flags.blocksClockOut || flags.strictPlayback;
}

export interface TrainingLessonEstimateInput {
  readonly kind: TrainingContentKind;
  readonly durationSeconds?: number | null;
  readonly pageCount?: number | null;
  readonly wordCount?: number | null;
  readonly checklistLength?: number | null;
  readonly questionCount?: number | null;
}

/** Reading speed and per-item allowances behind the estimate. Generous on purpose: the number is
 * the "allotted time" a strict course measures overruns against, and an estimate that is too tight
 * punishes the learner for the author's guess. */
export const TRAINING_ESTIMATE = {
  wordsPerMinute: 180,
  minutesPerPage: 2,
  minutesPerChecklistItem: 1,
  minutesPerQuestion: 1,
  videoSlack: 1.2,
} as const;

/** Allotted minutes derived from the lessons, used when the author does not set one. Never < 1. */
export function estimateTrainingCourseMinutes(lessons: readonly TrainingLessonEstimateInput[]): number {
  let minutes = 0;
  for (const lesson of lessons) {
    switch (lesson.kind) {
      case 'video_url':
      case 'video_upload':
        minutes += ((lesson.durationSeconds ?? 0) / 60) * TRAINING_ESTIMATE.videoSlack;
        break;
      case 'document':
        minutes += (lesson.pageCount ?? 1) * TRAINING_ESTIMATE.minutesPerPage;
        break;
      case 'reading':
        minutes += (lesson.wordCount ?? 0) / TRAINING_ESTIMATE.wordsPerMinute;
        break;
      case 'checklist':
        minutes += (lesson.checklistLength ?? 1) * TRAINING_ESTIMATE.minutesPerChecklistItem;
        break;
      case 'quiz':
        minutes += (lesson.questionCount ?? 1) * TRAINING_ESTIMATE.minutesPerQuestion;
        break;
    }
  }
  return Math.max(1, Math.ceil(minutes));
}

/** "1h 20m", "45m", "30s" — a course or lesson length on a card or in the lesson list. */
export function formatTrainingDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** "4:12", "1:02:05" — a video's length next to a lesson, like a video site shows it. */
export function formatTrainingClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}
